import React, { useEffect, useRef, useState } from "react";

export interface FotoEntregaProps {
	value: File | null;
	onChange: (file: File | null) => void;
	disabled?: boolean;
}

/** Longest side of the photo that is sent (the receipt only needs a thumbnail-quality picture). */
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.8;

const loadImage = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
	const image = new Image();
	image.onload = () => resolve(image);
	image.onerror = () => reject(new Error("decode"));
	image.src = url;
});

/**
 * Downscales a photo to ≤ 1600 px JPEG 0.8 on the device, so a 12 MB phone shot does not travel whole.
 * If the browser cannot decode it (HEIC outside Safari) the original File is kept and the server does the work.
 */
export const shrinkPhoto = async (file: File): Promise<File> => {
	const url = URL.createObjectURL(file);
	try {
		const image = await loadImage(url);
		const scale = Math.min(1, MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
		if (scale === 1 && /jpe?g$/i.test(file.type) && file.size < 2_000_000) return file;
		const canvas = document.createElement("canvas");
		canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
		canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
		const context = canvas.getContext("2d");
		if (!context) return file;
		context.drawImage(image, 0, 0, canvas.width, canvas.height);
		const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
		if (!blob) return file;
		return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: Date.now() });
	} catch (decodeError) {
		return file;
	} finally {
		URL.revokeObjectURL(url);
	}
};

/** Optional photo of the delivery: "Tomar Foto" opens the camera on phones/tablets (file picker on a computer). */
const FotoEntrega: React.FC<FotoEntregaProps> = ({ value, onChange, disabled }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	/** Without "capture": the phone offers its gallery/files (a receipt or a photo taken earlier). */
	const galleryRef = useRef<HTMLInputElement>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [previewFailed, setPreviewFailed] = useState<boolean>(false);
	const [working, setWorking] = useState<boolean>(false);

	useEffect(() => {
		setPreviewFailed(false);
		if (!value) { setPreview(null); return; }
		const url = URL.createObjectURL(value);
		setPreview(url);
		return () => URL.revokeObjectURL(url);
	}, [value]);

	const pick = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files && event.target.files[0];
		event.target.value = "";
		if (!file) return;
		setWorking(true);
		try {
			onChange(await shrinkPhoto(file));
		} finally {
			setWorking(false);
		}
	};

	const sizeLabel = (file: File): string => file.size > 1_000_000 ? (file.size / 1_000_000).toFixed(1) + " MB" : Math.round(file.size / 1000) + " KB";

	return <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
		<input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} disabled={disabled} onChange={pick} />
		<input ref={galleryRef} type="file" accept="image/*" style={{ display: "none" }} disabled={disabled} onChange={pick} />
		{value ? <>
			{preview && !previewFailed
				? <img src={preview} alt="Foto de la entrega" onError={() => setPreviewFailed(true)}
					style={{ maxWidth: "120px", maxHeight: "120px", borderRadius: "8px", border: "1px solid var(--wa-filo)", objectFit: "cover" }} />
				: <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "120px", height: "80px", borderRadius: "8px", border: "1px solid var(--wa-filo)", background: "var(--wa-papel-2)", fontSize: "12px", color: "var(--wa-apagado)", textAlign: "center", padding: "4px" }}>Foto lista (sin vista previa)</span>}
			<div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
				<span style={{ fontSize: "12px", color: "var(--wa-apagado)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "220px" }}>{value.name} · {sizeLabel(value)}</span>
				<div style={{ display: "flex", gap: "6px" }}>
					<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={disabled || working} onClick={() => inputRef.current?.click()}>Otra Foto</button>
					<button type="button" className="btn btn-link btn-sm" disabled={disabled} style={{ textDecoration: "none" }} onClick={() => onChange(null)}>Quitar</button>
				</div>
			</div>
		</> : <>
			<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={disabled || working} onClick={() => inputRef.current?.click()}>
				{working ? "Preparando…" : "Tomar Foto"}
			</button>
			<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={disabled || working} onClick={() => galleryRef.current?.click()}>Subir Foto o Comprobante</button>
		</>}
	</div>;
};

export default FotoEntrega;
